import React, { useContext, useEffect, useRef, useState } from 'react';
import LoginFormLayout from '../../../components/LoginFormLayout/LoginFormLayout';
import { CustomRegistrationFormInput } from '../../../components';
import CustomFormErrorMessage from '../../../components/CustomFormErrorMessage/CustomFormErrorMessage';
import { Button, Flex, Form, Input, Typography } from 'antd';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import { useNavigate } from 'react-router-dom';
import CreateNewPassword from '../CreateNewPasswordForm/CreateNewPassword';
import PropTypes from 'prop-types';

const { Text } = Typography;

const ForgotPasswordForm = ({
  onReset,
  onSendOTP,
  email,
  isMobileLogin = false,
}) => {
  const [step, setStep] = useState('email');
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const navigate = useNavigate();
  const otpRef = useRef(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [emailId, setEmailId] = useState(email);
  const [timer, setTimer] = useState(180);
  const [isResendEnabled, setIsResendEnabled] = useState(false);

  useEffect(() => {
    if (email) {
      setEmailId(email);
      form.setFieldsValue({ email });
    }
  }, [email, form]);

  const onFinish = async values => {
    setLoading(true);
    switch (step) {
      case 'email':
        handleSendOTP(values, step);
        break;
      case 'otp':
        handleSendOTP(values, step);
        break;
      default:
        break;
    }
  };

  const handleSendOTP = async (values, step = 'email') => {
    try {
      const { email, otp, new_password, confirm_password } = values;
      const message =
        step === 'otp'
          ? await onReset({
              email: emailId,
              otp,
              new_password,
              confirm_password,
            })
          : await onSendOTP(email);

      step === 'email' && setEmailId(email);
      step === 'otp'
        ? addErrorMessage(
            message,
            'success',
            'password-reset-notification',
            3,
            true,
            true
          )
        : addErrorMessage(message, 'success');
      step === 'email' && setStep('otp');
      step === 'otp' && navigate('login');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (isResendEnabled) {
      handleSendOTP({ email: form.getFieldValue('email') }, 'resend');
      setTimer(180); // restart timer
    }
  };

  const formatTime = sec => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  React.useEffect(() => {
    if (timer > 0) {
      setIsResendEnabled(false);
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setIsResendEnabled(true);
    }
  }, [timer]);

  const handleBack = () => {
    if (step === 'email') {
      navigate(isMobileLogin ? '/device-registration-login' : '/login');
    } else if (step === 'otp') {
      setStep('email');
      setTimer(180);
      form.resetFields();
      if (emailId) {
        form.setFieldsValue({ email: emailId });
      }
    }
  };

  return (
    <LoginFormLayout
      onFinish={onFinish}
      title={step === 'email' ? 'Forgot Password' : 'Create new password'}
      form={form}
      onBack={handleBack}
      isMobileLogin={isMobileLogin}
    >
      {step === 'email' && (
        <CustomRegistrationFormInput
          label={<Text strong>Registered email</Text>}
          placeholder="e.g.: name@email.com"
          name="email"
          readOnly={Boolean(emailId)}
          rules={[
            {
              required: true,
              message: (
                <CustomFormErrorMessage message="Please input your email" />
              ),
            },
            {
              type: 'email',
              message: (
                <CustomFormErrorMessage message="Please enter a valid email address" />
              ),
            },
          ]}
        />
      )}
      {step === 'otp' && (
        <Flex className="full-width" align="center" vertical>
          <Text style={{ paddingBottom: 24 }} align="center">
            Please enter the OTP sent to your email in order to reset your
            password.
          </Text>
          <Form.Item
            name="otp"
            rules={[
              {
                required: true,
                message: <CustomFormErrorMessage message="Please enter otp" />,
              },
            ]}
            className="full-width register-link"
            style={{ marginBottom: 12 }}
          >
            <Input.OTP length={4} size="large" autoFocus ref={otpRef} />
          </Form.Item>
          <small>
            <b>{formatTime(timer)}</b>
          </small>
          <Button
            type="link"
            className="btn-login-link"
            onClick={handleResend}
            disabled={!isResendEnabled}
          >
            <Text>Did not receive OTP?</Text> Resend OTP
          </Button>
          <CreateNewPassword
            form={form}
            title="Enter new password"
            style={{ textAlign: 'center', margin: '44px 0 32px' }}
            titleLevel={3}
          />
        </Flex>
      )}
      <Form.Item>
        <Button type="primary" htmlType="submit" block loading={loading}>
          {step === 'email' ? 'Send OTP' : 'Update new Password'}
        </Button>
      </Form.Item>
    </LoginFormLayout>
  );
};

ForgotPasswordForm.propTypes = {
  onReset: PropTypes.func.isRequired,
  onSendOTP: PropTypes.func.isRequired,
  email: PropTypes.string,
  isMobileLogin: PropTypes.bool,
};

export default ForgotPasswordForm;
